import { NotificationManager } from 'react-notifications';
import axios from 'axios';
import isArray from 'lodash/isArray';
import { IFlowData } from 'src/components/ui/Form/types';
import { ISelectOptions } from 'src/components/ui/Select/createSelect.types';
import { IQueryParams } from 'src/types';
import { attempt } from 'src/utils/attempts';

import { IPayoutInvoiceHistory, IPayoutOrderId, IPayoutOrderListData } from './types';

export const fetchPayoutOrdersRequest = async ({
  amountFrom,
  amountTo,
  createdAtFrom,
  createdAtTo,
  merchantIds,
  merchantAccountIds,
  statuses,
  metaFilter,
  page,
  size,
  sort,
  ...params
}: IQueryParams): Promise<IPayoutOrderListData | null> => {
  try {
    const filters = { ...params };

    if (amountFrom || amountTo) {
      filters.amountFilter = {
        amountFrom,
        amountTo,
      };
    }
    if (createdAtFrom || createdAtTo) {
      filters.dateFilter = {
        createdAtFrom,
        createdAtTo,
      };
    }
    if (merchantIds) {
      filters.merchantIds = isArray(merchantIds) ? merchantIds : [merchantIds];
    }
    if (merchantAccountIds) {
      filters.merchantAccountIds = isArray(merchantAccountIds) ? merchantAccountIds : [merchantAccountIds];
    }
    if (statuses) {
      filters.statuses = isArray(statuses) ? statuses : [statuses];
    }
    if (metaFilter) {
      filters.metaFilter = attempt(() => JSON.parse(metaFilter), {});
    }

    const res = await axios.post('api/v1/payout/payout-order-search', filters, { params: { page, size, sort } });
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch payout orders");
    return null;
  }
};

export const fetchPayoutOrderByIdRequest = async (id: string): Promise<IPayoutOrderId | undefined> => {
  try {
    const res = await axios.get(`api/v1/payout/payout-order-search/${id}`);
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch payout order");
    return;
  }
};

export const fetchPayoutOrderHistoryRequest = async ({
  orderIdentity: payoutOrderIdentity,
}: IFlowData): Promise<IPayoutInvoiceHistory | null> => {
  try {
    const res = await axios.post('api/v1/payout/invoice-history', { payoutOrderIdentity });
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch invoice history");
    return null;
  }
};

export const fetchDictMerchant = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/search-filter/merchant', data);
  return res.data;
};

export const fetchDictPayoutStatus = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/payout/search-filter/status', data);
  return res.data?.map((status: string) => ({ value: status, label: status }));
};

export const fetchDictPayoutCurrency = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/payout/search-filter/currency', data);
  return res.data?.map((currency: string) => ({ value: currency, label: currency }));
};

export const fetchDictPayoutMerchantAccount = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/payout/search-filter/merchant-account', data);
  return res.data;
};

export const fetchDictPayoutFormNames = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/payout/search-filter/form-names', data);
  return res.data?.map((name: string) => ({ value: name, label: name }));
};

export const fetchDictPayoutSystemGroupForm = async (data: IFlowData): Promise<ISelectOptions[]> => {
  const res = await axios.get('api/v1/payout/search-filter/system-group-form', data);
  return res.data;
};

export const resendPayoutCallback = async (data: { payoutOrderId: string }): Promise<void> => {
  return axios.post('api/v1/payout/init-resend-callback', data);
};
